import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import { Button } from "@/common/components/ui/button";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Building2,
  Plus,
  Trash2,
} from "lucide-react";
import DatePickerInput from "@/common/components/features/date-picker-input";

interface Address {
  address: string;
  addressLine2?: string;
  landmark?: string;
  city: string;
  state: string;
  pincode?: string;
  country?: string;
  isDefaultAddress?: boolean;
}

interface UserData {
  name: string;
  email: string;
  mobile: string;
  status: string;
  kycStatus: string;
  addresses: Address[];
  pan: string;
  aadhar: string;
  dob: string;
  gender: "Male" | "Female" | "Other";
  occupation: string;
  incomeRange: string;
}

interface PersonalInfoTabProps {
  userData: UserData;
  setUserData: (data: any) => void;
  readOnly?: boolean;
  validationErrors?: Record<string, string>;
}

const PersonalInfoTab = ({
  userData,
  setUserData,
  readOnly = false,
  validationErrors = {},
}: PersonalInfoTabProps) => {
  const readOnlyClass = readOnly ? "bg-slate-50 cursor-not-allowed" : "";

  const handleAddressChange = (
    index: number,
    field: keyof Address,
    value: any
  ) => {
    const newAddresses = [...(userData.addresses || [])];
    newAddresses[index] = { ...newAddresses[index], [field]: value };
    setUserData({ ...userData, addresses: newAddresses });
  };

  console.log("userData", userData);

  const handleAddAddress = () => {
    const newAddress: Address = {
      address: "",
      addressLine2: "",
      landmark: "",
      city: "",
      state: "",
      pincode: "",
      country: "",
      isDefaultAddress: false,
    };
    setUserData({
      ...userData,
      addresses: [...(userData.addresses || []), newAddress],
    });
  };

  const handleRemoveAddress = (index: number) => {
    const newAddresses = [...(userData.addresses || [])];
    newAddresses.splice(index, 1);
    setUserData({ ...userData, addresses: newAddresses });
  };

  return (
    <div className="space-y-6">
      {/* Contact & Basic Details Section */}
      <Card className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <CardHeader className="border-b border-slate-100 pb-4">
          <CardTitle className="flex items-center gap-2 text-base font-bold text-slate-800">
            <User className="h-5 w-5 text-primary" />
            Contact & Basic Details
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-x-8 gap-y-6 p-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Full Name *
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={userData.name}
                onChange={e =>
                  setUserData({ ...userData, name: e.target.value })
                }
                className={`h-11 border-slate-200 pl-10 font-medium transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass} ${
                  validationErrors.name
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
                disabled={readOnly}
                required
                placeholder="Enter Full Name"
              />
            </div>
            {validationErrors.name && (
              <p className="text-xs text-red-600">{validationErrors.name}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Email Address *
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                type="email"
                value={userData.email}
                onChange={e =>
                  setUserData({ ...userData, email: e.target.value })
                }
                className={`h-11 border-slate-200 pl-10 font-medium transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass} ${
                  validationErrors.email
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
                disabled={readOnly}
                required
                placeholder="Enter Email Address"
              />
            </div>
            {validationErrors.email && (
              <p className="text-xs text-red-600">{validationErrors.email}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Mobile Number *
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={userData.mobile}
                onChange={e =>
                  setUserData({ ...userData, mobile: e.target.value })
                }
                className={`h-11 border-slate-200 pl-10 font-medium transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass} ${
                  validationErrors.mobile
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
                disabled={readOnly}
                required
                placeholder="Enter Mobile Number"
              />
            </div>
            {validationErrors.mobile && (
              <p className="text-xs text-red-600">{validationErrors.mobile}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Date of Birth
            </Label>
            <DatePickerInput
              value={userData.dob}
              onChange={value => setUserData({ ...userData, dob: value })}
              error={validationErrors.dob}
              className={`h-11 border-slate-200 transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass}`}
              disabled={readOnly}
            />
          </div>
        </CardContent>
      </Card>

      {/* Address Details Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            <span className="text-base font-bold text-slate-800">
              Address Details
            </span>
          </div>
          <Button
            size="sm"
            className="bg-primary hover:bg-primary/90"
            onClick={handleAddAddress}
            disabled={readOnly}
            type="button"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add New Address
          </Button>
        </div>

        {userData.addresses?.map((address, index) => (
          <Card
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className="rounded-xl border border-slate-200 bg-white shadow-sm transition-all hover:border-primary/20"
          >
            <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100 pb-4">
              <CardTitle className="text-sm font-bold text-slate-700">
                Address #{index + 1}
              </CardTitle>
              {index > 0 && !readOnly && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:bg-red-50 hover:text-red-600"
                  onClick={() => handleRemoveAddress(index)}
                  type="button"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-6 p-6">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id={`isDefaultAddress-${index}`}
                  checked={address.isDefaultAddress ?? false}
                  onChange={e =>
                    handleAddressChange(
                      index,
                      "isDefaultAddress",
                      e.target.checked
                    )
                  }
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  disabled={readOnly}
                />
                <Label
                  htmlFor={`isDefaultAddress-${index}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Default
                </Label>
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Address Line 1
                </Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={address.address}
                    onChange={e =>
                      handleAddressChange(index, "address", e.target.value)
                    }
                    className={`h-11 border-slate-200 pl-10 font-medium transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass}`}
                    disabled={readOnly}
                    placeholder="House No, Building, Street"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Address Line 2
                </Label>
                <Input
                  value={address.addressLine2 ?? ""}
                  onChange={e =>
                    handleAddressChange(index, "addressLine2", e.target.value)
                  }
                  className="h-10 bg-slate-50 transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:bg-white focus:ring-primary/10"
                  disabled={readOnly}
                  placeholder="Apartment, Studio, or Floor"
                />
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Area / Landmark
                  </Label>
                  <Input
                    value={address.landmark ?? ""}
                    onChange={e =>
                      handleAddressChange(index, "landmark", e.target.value)
                    }
                    className="h-10 bg-slate-50 transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:bg-white focus:ring-primary/10"
                    placeholder="Near Landmark"
                    disabled={readOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    City
                  </Label>
                  <Input
                    value={address.city}
                    onChange={e =>
                      handleAddressChange(index, "city", e.target.value)
                    }
                    className={`h-11 border-slate-200 font-medium transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass}`}
                    disabled={readOnly}
                    placeholder="City"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Pincode
                  </Label>
                  <Input
                    value={address.pincode ?? ""}
                    onChange={e =>
                      handleAddressChange(index, "pincode", e.target.value)
                    }
                    className={`h-11 border-slate-200 font-medium transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass}`}
                    disabled={readOnly}
                    placeholder="e.g. 400001"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    State
                  </Label>
                  <Input
                    value={address.state}
                    onChange={e =>
                      handleAddressChange(index, "state", e.target.value)
                    }
                    className={`h-11 border-slate-200 font-medium transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass}`}
                    disabled={readOnly}
                    placeholder="State"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Country
                  </Label>
                  <Input
                    value={address.country ?? ""}
                    onChange={e =>
                      handleAddressChange(index, "country", e.target.value)
                    }
                    className={`h-11 border-slate-200 font-medium transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass}`}
                    disabled={readOnly}
                    placeholder="Country"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default PersonalInfoTab;
